import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button, Text, VStack, theme } from '@squire/ui';

import {
  BottomSheet,
  BottomSheetInput,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { usePrevious } from '@/hooks/shared/usePrevious';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import composeRefs from '@/utils/composeRefs';

import { ScreenHeader } from '../ScreenHeader';
import { styles } from './styles';
import type { PromoCodeModalProps } from './types';

export const PromoCodeModal = forwardRef<BottomSheetRef, PromoCodeModalProps>(
  ({ onApply, isLoading, onClose }, ref) => {
    const { t } = useScreenTranslations('PromoCodeModal');

    const bottomSheetRef = useRef<BottomSheetRef>(null);

    const [code, setCode] = useState('');

    const wasLoading = usePrevious(isLoading);

    const handleCodeChange = (value: string) => {
      // Convert to uppercase and remove spaces
      const formattedValue = value.toUpperCase().replace(/\s/g, '');
      setCode(formattedValue);
    };

    const handleApply = () => {
      if (code.trim()) {
        onApply(code.trim());
      }
    };

    const canApply = () => {
      return code.trim().length > 0;
    };

    const handleClose = useCallback(() => {
      bottomSheetRef.current?.close();
      setCode('');
      onClose?.();
    }, [onClose]);

    useEffect(() => {
      if (wasLoading && !isLoading) {
        handleClose();
      }
    }, [wasLoading, isLoading, handleClose]);

    return (
      <BottomSheet
        accessibilityLabel={t('title')}
        dismissible
        onDismiss={handleClose}
        hideHandle
        containerStyle={styles.bottomSheet}
        ref={composeRefs(ref, bottomSheetRef)}
      >
        <ScreenHeader
          title={t('title')}
          onClose={handleClose}
          paddingVertical={theme.spacing[6]}
        />

        {/* Code Input */}
        <VStack style={styles.inputContainer}>
          <Text
            variant="sStrong"
            color="textSecondary"
            style={styles.inputLabel}
          >
            {t('codeLabel')}
          </Text>
          <BottomSheetInput
            value={code}
            onChangeText={handleCodeChange}
            placeholder={t('codePlaceholder')}
            containerStyle={styles.input}
            accessibilityLabel={t('accessibility.codeInput')}
            accessibilityHint={t('accessibility.codeInputHint')}
            autoCapitalize="characters"
            autoCorrect={false}
          />
        </VStack>

        {/* Apply Button */}
        <Button
          onPress={handleApply}
          disabled={!canApply() || isLoading}
          loading={isLoading}
          accessibilityRole="button"
          accessibilityLabel={t('accessibility.apply')}
          variant="primary"
        >
          {t('apply')}
        </Button>
      </BottomSheet>
    );
  },
);

PromoCodeModal.displayName = 'PromoCodeModal';
